import {
  prescriptionService,
  type CreateDrugPrescriptionsRequest,
  type DrugPrescriptionCreateRequest,
  type FinalizedPrescriptionResponse,
  type FinalizedPrescriptionSearchRequest,
  type PrescriptionImageExclusionReason,
  type PrescriptionListResponse,
  type PrescriptionSearchRequest,
  type UpdatePrescriptionDrugRequest,
  type UpdatePrescriptionImageRequest,
} from '@/entities/prescription/api/prescription.api';
import type { Pageable } from '@/entities/user/api/user.api';
import { usePaginatedQuery } from '@/shared/hooks/usePaginatedQuery';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const prescriptionKeys = {
  all: ['prescriptions'] as const,
  lists: () => [...prescriptionKeys.all, 'list'] as const,
  list: (search: PrescriptionSearchRequest, pageable: Pageable) =>
    [...prescriptionKeys.lists(), search, pageable] as const,
  finalizedLists: () => [...prescriptionKeys.all, 'finalized', 'list'] as const,
  finalizedList: (
    search: FinalizedPrescriptionSearchRequest,
    pageable: Pageable,
  ) => [...prescriptionKeys.finalizedLists(), search, pageable] as const,
  imageDetails: () => [...prescriptionKeys.all, 'image-detail'] as const,
  imageDetail: (code: string) =>
    [...prescriptionKeys.imageDetails(), code] as const,
  drugs: (code: string, refYm: string) =>
    [...prescriptionKeys.all, 'drugs', code, refYm] as const,
  previousDrugs: (code: string) =>
    [...prescriptionKeys.all, 'previous-drugs', code] as const,
  finalizedDetails: () =>
    [...prescriptionKeys.all, 'finalized', 'detail'] as const,
  finalizedDetail: (id: number) =>
    [...prescriptionKeys.finalizedDetails(), id] as const,
  finalizedPharmaCompanies: (settlementYearMonth: string) =>
    [
      ...prescriptionKeys.all,
      'finalized',
      'pharma-companies',
      settlementYearMonth,
    ] as const,
};

export interface PrescriptionListParams {
  search: PrescriptionSearchRequest;
  pageable: Pageable;
}

export interface FinalizedPrescriptionListParams {
  search: FinalizedPrescriptionSearchRequest;
  pageable: Pageable;
}

/**
 * 처방 검수 목록.
 * PageResponse 외에 ocrMatchStats를 함께 내려주므로 전용 query를 사용한다.
 */
export const usePrescriptionList = ({
  search,
  pageable,
}: PrescriptionListParams) =>
  useQuery<PrescriptionListResponse>({
    queryKey: prescriptionKeys.list(search, pageable),
    queryFn: () => prescriptionService.getPrescriptions(search, pageable),
  });

/** 확정 처방 내역 목록 — 레거시 `prescriptionDetails.js` */
export const useFinalizedPrescriptionList = ({
  search,
  pageable,
}: FinalizedPrescriptionListParams) =>
  usePaginatedQuery<
    FinalizedPrescriptionSearchRequest,
    FinalizedPrescriptionResponse
  >({
    queryKeyPrefix: prescriptionKeys.finalizedLists(),
    search,
    pageable,
    queryFn: async ({ search: s, pageable: p }) =>
      prescriptionService.getFinalizedPrescriptions(s, p),
  });

export const usePrescriptionImageDetail = (
  prescriptionImageCode: string | null,
) =>
  useQuery({
    queryKey: prescriptionImageCode
      ? prescriptionKeys.imageDetail(prescriptionImageCode)
      : [],
    queryFn: async () => {
      if (!prescriptionImageCode) throw new Error('code required');
      return prescriptionService.getPrescriptionImageDetail(
        prescriptionImageCode,
      );
    },
    enabled: !!prescriptionImageCode,
  });

export const usePrescriptionDrugs = (
  prescriptionImageCode: string | null,
  refYm: string | null,
) =>
  useQuery({
    queryKey:
      prescriptionImageCode && refYm
        ? prescriptionKeys.drugs(prescriptionImageCode, refYm)
        : [],
    queryFn: async () => {
      if (!prescriptionImageCode || !refYm)
        throw new Error('code/refYm required');
      return prescriptionService.getPrescriptionDrugs(
        prescriptionImageCode,
        refYm,
      );
    },
    enabled: !!prescriptionImageCode && !!refYm,
  });

/**
 * 이전 처방 의약품 조회 — 처방 검수 상세의 [이전 처방 불러오기] 모달.
 * 모달을 열 때만 조회하도록 enabled 로 제어한다.
 */
export const usePreviousPrescriptionDrugs = (
  prescriptionImageCode: string | null,
  enabled: boolean,
) =>
  useQuery({
    queryKey: prescriptionKeys.previousDrugs(prescriptionImageCode ?? ''),
    queryFn: async () => {
      if (!prescriptionImageCode) throw new Error('code required');
      return prescriptionService.getPreviousPrescriptionDrugs(
        prescriptionImageCode,
      );
    },
    enabled: enabled && !!prescriptionImageCode,
  });

/** 확정 처방이 있는 제약사 목록 — export 다이얼로그 필터 */
export const usePharmaCompaniesWithFinalizedPrescriptions = (
  settlementYearMonth: string | null,
) =>
  useQuery({
    queryKey: settlementYearMonth
      ? prescriptionKeys.finalizedPharmaCompanies(settlementYearMonth)
      : [],
    queryFn: async () => {
      if (!settlementYearMonth) throw new Error('settlementYearMonth required');
      return prescriptionService.getPharmaCompaniesWithFinalizedPrescriptions(
        settlementYearMonth,
      );
    },
    enabled: !!settlementYearMonth,
  });

/**
 * 확정 처방 내역 엑셀.
 * 서버가 xlsx를 만들 때까지 기다리므로 버튼 로딩을 이 mutation에 묶는다.
 */
export const useExportFinalizedPrescriptionsExcel = () =>
  useMutation({
    mutationFn: ({
      settlementYearMonth,
      pharmaCompanyId,
      parentCompanyId,
      companyName,
    }: {
      settlementYearMonth: string;
      pharmaCompanyId?: number;
      parentCompanyId?: number;
      companyName?: string;
    }) =>
      prescriptionService.exportFinalizedPrescriptionsExcel(
        settlementYearMonth,
        pharmaCompanyId,
        parentCompanyId,
        companyName,
      ),
  });

/**
 * 확정 처방 이미지 ZIP.
 * 서버가 아카이브를 만들 때까지 기다리므로 버튼 로딩을 이 mutation에 묶는다.
 */
export const useExportFinalizedPrescriptionImages = () =>
  useMutation({
    mutationFn: ({
      settlementYearMonth,
      pharmaCompanyId,
      parentCompanyId,
      companyName,
    }: {
      settlementYearMonth: string;
      pharmaCompanyId?: number;
      parentCompanyId?: number;
      companyName?: string;
    }) =>
      prescriptionService.exportFinalizedPrescriptionImages(
        settlementYearMonth,
        pharmaCompanyId,
        parentCompanyId,
        companyName,
      ),
  });

export const useFinalizedPrescriptionDetail = (prescriptionId: number | null) =>
  useQuery({
    queryKey:
      prescriptionId != null
        ? prescriptionKeys.finalizedDetail(prescriptionId)
        : [],
    queryFn: async () => {
      if (prescriptionId == null) throw new Error('id required');
      return prescriptionService.getFinalizedPrescriptionDetail(prescriptionId);
    },
    enabled: prescriptionId != null,
  });

export const useUpdatePrescriptionDrug = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      prescriptionMrId,
      data,
      prescriptionImageCode,
      refYm,
    }: {
      prescriptionMrId: number;
      data: UpdatePrescriptionDrugRequest;
      prescriptionImageCode: string;
      refYm: string;
    }) => {
      await prescriptionService.updatePrescriptionDrug(prescriptionMrId, data);
      return { prescriptionImageCode, refYm };
    },
    onSuccess: ({ prescriptionImageCode, refYm }) => {
      queryClient.invalidateQueries({
        queryKey: prescriptionKeys.drugs(prescriptionImageCode, refYm),
      });
      queryClient.invalidateQueries({
        queryKey: prescriptionKeys.imageDetail(prescriptionImageCode),
      });
    },
  });
};

export const useUpdatePrescriptionImage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      prescriptionImageCode,
      data,
    }: {
      prescriptionImageCode: string;
      data: UpdatePrescriptionImageRequest;
    }) =>
      prescriptionService.updatePrescriptionImage(prescriptionImageCode, data),
    onSuccess: (_, { prescriptionImageCode }) => {
      queryClient.invalidateQueries({
        queryKey: prescriptionKeys.imageDetail(prescriptionImageCode),
      });
      queryClient.invalidateQueries({ queryKey: prescriptionKeys.lists() });
    },
  });
};

/**
 * 처방 이미지·내역 삭제.
 * 성공 시 목록·상세 캐시를 무효화한다 (호출측에서 목록으로 이동).
 */
export const useDeletePrescriptionImage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (prescriptionImageCode: string) =>
      prescriptionService.deletePrescriptionImage(prescriptionImageCode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: prescriptionKeys.all });
    },
  });
};

export const useFinalizePrescription = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (prescriptionImageCode: string) =>
      prescriptionService.finalizePrescription({ prescriptionImageCode }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: prescriptionKeys.all });
    },
  });
};

/** 처방 검수 일괄 확정 — `POST /prescriptions/finalize-batch` */
export const useFinalizePrescriptionsBatch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (prescriptionImageIds: number[]) =>
      prescriptionService.finalizePrescriptions({ prescriptionImageIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: prescriptionKeys.all });
    },
  });
};

/** 정산년월 전체 확정 — `POST /prescriptions/finalize/settlement-month` */
export const useFinalizePrescriptionsBySettlementMonth = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (settlementYearMonth: string) =>
      prescriptionService.finalizePrescriptionsBySettlementMonth({
        settlementYearMonth,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: prescriptionKeys.all });
    },
  });
};

/** 처방 약 단건 등록 — 레거시 `addPrescribedDrug` */
export const useCreateDrugPrescription = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: DrugPrescriptionCreateRequest) =>
      prescriptionService.createDrugPrescription(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: prescriptionKeys.all });
    },
  });
};

/** 처방 약 단건 삭제 */
export const useDeletePrescriptionDrug = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (prescriptionMrId: number) =>
      prescriptionService.deletePrescriptionDrug(prescriptionMrId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: prescriptionKeys.all });
    },
  });
};

/** OCR·CSV 등으로 처방 약 목록 일괄 등록 */
export const useCreateDrugPrescriptions = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requests: CreateDrugPrescriptionsRequest[]) =>
      prescriptionService.createDrugPrescriptions(requests),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: prescriptionKeys.all });
    },
  });
};

/** 등록 처방 내역 조회 — 처방내역 다운로드 */
export const useRegisteredPrescriptions = () =>
  useMutation({
    mutationFn: ({
      settlementYearMonth,
      userEmail,
      clinicName,
    }: {
      settlementYearMonth: string;
      userEmail?: string;
      clinicName?: string;
    }) =>
      prescriptionService.getRegisteredPrescriptions(
        settlementYearMonth,
        userEmail,
        clinicName,
      ),
  });

/** 신규 처방 presigned URL 생성 */
export const useCreatePrescriptionPresignedUrls = () =>
  useMutation({
    mutationFn: (filenames: string[]) =>
      prescriptionService.createPresignedUrls(filenames),
  });

/** 신규 처방 등록 완료 */
export const useRegisterPrescriptionUpload = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: prescriptionService.registerPrescriptionUpload,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: prescriptionKeys.all });
    },
  });
};

/** 처방 이미지 코드 기준 약 목록 전체 삭제 — OCR 재등록 전 사용 */
export const useDeletePrescriptionDrugsByImageCode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (prescriptionImageCodes: string[]) =>
      prescriptionService.deletePrescriptionDrugs({ prescriptionImageCodes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: prescriptionKeys.all });
    },
  });
};

export const useCancelPrescriptionFinalization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (prescriptionImageCode: string) =>
      prescriptionService.cancelFinalizedPrescription(prescriptionImageCode),
    onSuccess: (_, prescriptionImageCode) => {
      queryClient.invalidateQueries({ queryKey: prescriptionKeys.all });
      queryClient.invalidateQueries({
        queryKey: prescriptionKeys.imageDetail(prescriptionImageCode),
      });
    },
  });
};

export const useFindDuplicateFinalizedDrugs = () =>
  useMutation({
    mutationFn: (prescriptionImageCode: string) =>
      prescriptionService.findDuplicateFinalizedPrescriptionDrugs(
        prescriptionImageCode,
      ),
  });

export const useAutoMatchingRetry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (prescriptionImageCode: string) =>
      prescriptionService.requestAutoMatchingRetry(prescriptionImageCode),
    onSuccess: (_, prescriptionImageCode) => {
      queryClient.invalidateQueries({
        queryKey: prescriptionKeys.imageDetail(prescriptionImageCode),
      });
    },
  });
};

/**
 * 처방 확정 제외 지정·해제.
 * 목록 필터·집계 표시가 바뀌므로 처방 캐시 전체를 무효화한다.
 */
export const useExcludePrescriptionImage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      prescriptionImageCode,
      reason,
    }: {
      prescriptionImageCode: string;
      reason: PrescriptionImageExclusionReason;
    }) =>
      prescriptionService.excludePrescriptionImage(
        prescriptionImageCode,
        reason,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: prescriptionKeys.all });
    },
  });
};

export const useCancelPrescriptionImageExclusion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (prescriptionImageCode: string) =>
      prescriptionService.cancelPrescriptionImageExclusion(
        prescriptionImageCode,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: prescriptionKeys.all });
    },
  });
};
